import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { GrafanaConfig, Dashboard, GrafanaDashboards, TimeRange, DashboardSelectorState } from '../types/grafana';
import monitoringConfig from '../../../../config/monitoring';

interface UseGrafanaOptions {
  grafanaUrl?: string;
  defaultOrgId?: number;
  defaultTheme?: 'light' | 'dark';
  tenant?: string;
}

export const useGrafana = (options: UseGrafanaOptions = {}) => {
  const {
    grafanaUrl = monitoringConfig.grafana.url,
    defaultOrgId,
    defaultTheme = 'light',
    tenant = 'streamdata.com' // Default tenant, should be resolved from hostname
  } = options;

  // State for dynamic orgId fetched from backend
  const [orgId, setOrgId] = useState<number | null>(defaultOrgId ?? null);
  const [isLoadingOrg, setIsLoadingOrg] = useState<boolean>(!defaultOrgId);
  const orgFetchedRef = useRef(false);

  // Fetch orgId from backend on mount
  useEffect(() => {
    if (defaultOrgId != null || orgFetchedRef.current) return;
    orgFetchedRef.current = true;

    const fetchOrg = async () => {
      try {
        setIsLoadingOrg(true);
        const url = `${monitoringConfig.backend.url}/api3.2/grafana/org`;
        const response = await axios.get(url, { timeout: 10000 });
        const data = response.data;
        if (data?.orgId) {
          setOrgId(data.orgId);
        } else {
          // Fallback to 1 if the backend doesn't return an orgId
          setOrgId(1);
        }
      } catch (error) {
        console.warn('[useGrafana] Failed to fetch org, falling back to orgId=1:', error);
        setOrgId(1);
      } finally {
        setIsLoadingOrg(false);
      }
    };

    fetchOrg();
  }, [defaultOrgId]);

  // State for dynamic dashboards
  const [dashboards, setDashboards] = useState<GrafanaDashboards>({});
  const [isLoadingDashboards, setIsLoadingDashboards] = useState<boolean>(true);
  const [dashboardsError, setDashboardsError] = useState<string | null>(null);

  // Time range options
  const timeRanges: TimeRange[] = useMemo(() => [
    { label: 'Last 5 minutes', value: '5m', from: 'now-5m', to: 'now' },
    { label: 'Last 15 minutes', value: '15m', from: 'now-15m', to: 'now' },
    { label: 'Last 30 minutes', value: '30m', from: 'now-30m', to: 'now' },
    { label: 'Last 1 hour', value: '1h', from: 'now-1h', to: 'now' },
    { label: 'Last 3 hours', value: '3h', from: 'now-3h', to: 'now' },
    { label: 'Last 6 hours', value: '6h', from: 'now-6h', to: 'now' },
    { label: 'Last 12 hours', value: '12h', from: 'now-12h', to: 'now' },
    { label: 'Last 24 hours', value: '24h', from: 'now-24h', to: 'now' },
    { label: 'Last 2 days', value: '2d', from: 'now-2d', to: 'now' },
    { label: 'Last 7 days', value: '7d', from: 'now-7d', to: 'now' }
  ], []);

  // Fetch dashboards dynamically from the backend API
  const fetchDashboards = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingDashboards(true);
      setDashboardsError(null);

      console.log('[useGrafana] Fetching dashboards from API...');

      const url = `${monitoringConfig.backend.url}/api3.2/grafana/dashboards${forceRefresh ? '?forceRefresh=true' : ''}`;

      const response = await axios.get(url, {
        timeout: 15000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const { dashboards: dashboardList } = response.data;

      // Transform backend response to match our Dashboard interface
      const transformedDashboards: GrafanaDashboards = {};

      if (Array.isArray(dashboardList)) {
        dashboardList.forEach((dashboard: { uid: string; title: string; url: string; tags?: string[]; folderTitle?: string }) => {
          transformedDashboards[dashboard.uid] = {
            uid: dashboard.uid,
            title: dashboard.title,
            url: dashboard.url,
            tags: dashboard.tags || [],
            category: dashboard.folderTitle || 'General',
            description: dashboard.tags?.join(', ') || ''
          };
        });
      }

      console.log(`[useGrafana] Successfully loaded ${Object.keys(transformedDashboards).length} dashboards`);
      setDashboards(transformedDashboards);

    } catch (error) {
      console.error('[useGrafana] Error fetching dashboards:', error);
      setDashboardsError(error instanceof Error ? error.message : 'Failed to load dashboards');

      // Fallback to empty dashboards on error
      setDashboards({});
    } finally {
      setIsLoadingDashboards(false);
    }
  }, []);

  // Fetch dashboards on mount
  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  // Get first available dashboard dynamically
  const getDefaultDashboard = useCallback(() => {
    const availableKeys = Object.keys(dashboards);
    return availableKeys.length > 0 ? availableKeys[0] : 'website-monitoring';
  }, [dashboards]);

  // Dashboard selector state
  const [dashboardState, setDashboardState] = useState<DashboardSelectorState>(() => ({
    selectedDashboard: getDefaultDashboard(),
    timeRange: timeRanges[6], // Default to last 12 hours
    refreshInterval: '30s',
    isFullscreen: false,
    isEditMode: false
  }));

  // Generate Grafana dashboard URL with tenant information
  const generateDashboardUrl = useCallback((
    dashboardKey: string,
    config: Partial<GrafanaConfig> = {}
  ): string => {
    const dashboard = dashboards[dashboardKey];
    if (!dashboard) {
      console.warn(`Dashboard ${dashboardKey} not found`);
      return '';
    }

    const baseConfig: GrafanaConfig = {
      baseUrl: grafanaUrl,
      orgId: orgId ?? 1,
      theme: defaultTheme,
      kiosk: true,
      ...config
    };

    // Build URL parameters
    const params = new URLSearchParams();
    
    if (baseConfig.orgId) params.set('orgId', baseConfig.orgId.toString());
    if (baseConfig.theme) params.set('theme', baseConfig.theme);
    if (baseConfig.from) params.set('from', baseConfig.from);
    if (baseConfig.to) params.set('to', baseConfig.to);
    if (baseConfig.refresh) params.set('refresh', baseConfig.refresh);
    
    // Add iframe optimization parameters for better embedding
    if (config.editMode) {
      // In edit mode, authenticate as admin and show editing UI
      params.set('kiosk', 'false'); // Show Grafana header for save access
      params.set('edit', 'true'); // Enable dashboard editing
      params.set('theme', 'light'); // Ensure consistent theme
      // Add admin credentials for editing (this will be handled by URL construction)
    } else {
      // In view mode, use anonymous viewer access with TV kiosk
      params.set('kiosk', ''); // Full kiosk mode — hides all Grafana chrome
      params.set('autofitpanels', 'true'); // Auto-fit panels to container size
      params.set('theme', 'light'); // Ensure consistent theme
    }
    
    // Add tenant information for multi-tenancy support
    params.set('tenant', tenant);

    // Construct full URL with authentication for edit mode
    // Check if dashboard URL already includes the base URL to avoid duplication
    const dashboardPath = dashboard.url.startsWith(baseConfig.baseUrl)
      ? dashboard.url.substring(baseConfig.baseUrl.length)
      : dashboard.url;

    const url = `${baseConfig.baseUrl}${dashboardPath}?${params.toString()}`;
    return url;
  }, [dashboards, grafanaUrl, orgId, defaultTheme, tenant]);

  // Get current dashboard URL
  const currentDashboardUrl = useMemo(() => {
    return generateDashboardUrl(dashboardState.selectedDashboard, {
      from: dashboardState.timeRange.from,
      to: dashboardState.timeRange.to,
      refresh: dashboardState.refreshInterval,
      editMode: dashboardState.isEditMode
    });
  }, [dashboardState, generateDashboardUrl]);

  // Update selected dashboard
  const selectDashboard = useCallback((dashboardKey: string) => {
    if (dashboards[dashboardKey]) {
      setDashboardState(prev => ({
        ...prev,
        selectedDashboard: dashboardKey
      }));
    }
  }, [dashboards]);

  // Update time range
  const setTimeRange = useCallback((timeRange: TimeRange) => {
    setDashboardState(prev => ({
      ...prev,
      timeRange
    }));
  }, []);

  // Update refresh interval
  const setRefreshInterval = useCallback((interval: string) => {
    setDashboardState(prev => ({
      ...prev,
      refreshInterval: interval
    }));
  }, []);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    setDashboardState(prev => ({
      ...prev,
      isFullscreen: !prev.isFullscreen
    }));
  }, []);

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    setDashboardState(prev => ({
      ...prev,
      isEditMode: !prev.isEditMode
    }));
  }, []);

  // Get available dashboards as array
  const availableDashboards = useMemo(() => {
    return Object.entries(dashboards).map(([key, dashboard]) => ({
      key,
      ...dashboard
    }));
  }, [dashboards]);

  // Get current dashboard info
  const currentDashboard = useMemo(() => {
    return dashboards[dashboardState.selectedDashboard];
  }, [dashboards, dashboardState.selectedDashboard]);

  // Exit edit mode (user saves via Grafana's native save button)
  const saveDashboard = useCallback(async () => {
    try {
      // Since we can't reliably save from outside the iframe due to CORS,
      // the user should use Grafana's native save button in edit mode
      // This function just exits edit mode
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay
      return true;
    } catch (error) {
      console.error('Error in save process:', error);
      return false;
    }
  }, []);

  return {
    // State
    dashboardState,
    currentDashboardUrl,
    currentDashboard,
    availableDashboards,
    timeRanges,
    isLoadingDashboards,
    isLoadingOrg,
    dashboardsError,
    orgId,

    // Actions
    selectDashboard,
    setTimeRange,
    setRefreshInterval,
    toggleFullscreen,
    toggleEditMode,
    saveDashboard,
    generateDashboardUrl,
    refreshDashboards: fetchDashboards
  };
};